import type {
  Brand,
  BrandCreditAccount,
  BrandCreditAccountsSummary,
  CreditAccountsTableSummaryQuery,
  ExpenseBudgetCategory,
  GetCreditAccountsQuery,
  OurWorkBudgetCategory,
  Team,
} from "@src/__generated__/graphql";
import { map } from "lodash";

type TCreditAccount = NonNullable<
  GetCreditAccountsQuery["brandCreditAccounts"]
>["data"][0];
type TBrand = NonNullable<TCreditAccount["brand"]>;
type TTeam = NonNullable<TCreditAccount["team"]>;
type TOurWorkBudgetCategory = NonNullable<
  TCreditAccount["ourWorkBudgetCategories"]
>[0];
type TExpenseBudgetCategory = NonNullable<
  TCreditAccount["expenseBudgetCategories"]
>[0];

export class CreditAccountModel {
  id: BrandCreditAccount["id"];
  brand: BrandModel;
  stats: BrandCreditAccount["stats"];

  title: BrandCreditAccount["title"];
  team: TeamModel;
  ourWorkBudgetCategories: OurWorkBudgetCategoryModel[];
  expenseBudgetCategories: ExpenseBudgetCategoryModel[];

  constructor(src: TCreditAccount) {
    this.id = src.id;
    this.brand = new BrandModel(src.brand);
    this.title = src.title;
    this.stats = src.stats;
    this.team = new TeamModel(src.team);
    this.ourWorkBudgetCategories = map(
      src.ourWorkBudgetCategories,
      (c) => new OurWorkBudgetCategoryModel(c),
    );
    this.expenseBudgetCategories = map(
      src.expenseBudgetCategories,
      (c) => new ExpenseBudgetCategoryModel(c),
    );
  }

  static fromGetCreditAccountsQuery(
    src: GetCreditAccountsQuery["brandCreditAccounts"],
  ) {
    return (
      src?.data.map((i) => {
        const {
          id,
          title,
          stats,
          team,
          expenseBudgetCategories,
          ourWorkBudgetCategories,
          brand,
        } = i;

        return new CreditAccountModel({
          id,
          title,
          stats,
          team,
          expenseBudgetCategories,
          ourWorkBudgetCategories,
          brand,
        });
      }) ?? []
    );
  }
}

class BrandModel {
  name: Brand["name"];
  id: Brand["id"];
  constructor(src: TBrand) {
    this.name = src.name;
    this.id = src.id;
  }
}

class TeamModel {
  id: Team["id"];
  name: Team["name"];
  constructor(src: TTeam) {
    this.id = src.id;
    this.name = src.name;
  }
}

class OurWorkBudgetCategoryModel {
  id: OurWorkBudgetCategory["id"];
  name: OurWorkBudgetCategory["name"];
  constructor(src: TOurWorkBudgetCategory) {
    this.name = src.name;
    this.id = src.id;
  }
}

class ExpenseBudgetCategoryModel {
  id: ExpenseBudgetCategory["id"];
  name: ExpenseBudgetCategory["name"];
  constructor(src: TExpenseBudgetCategory) {
    this.name = src.name;
    this.id = src.id;
  }
}

export class BrandCreditAccountsSummaryModel {
  brand_credit_accounts: BrandCreditAccountsSummary["brand_credit_accounts"];
  total_credit: BrandCreditAccountsSummary["total_credit"];
  total_debit: BrandCreditAccountsSummary["total_debit"];
  remaining_credit: BrandCreditAccountsSummary["remaining_credit"];
  constructor(
    src: CreditAccountsTableSummaryQuery["brandCreditAccountsSummary"],
  ) {
    this.brand_credit_accounts = src.brand_credit_accounts;
    this.total_credit = src.total_credit;
    this.total_debit = src.total_debit;
    this.remaining_credit = src.remaining_credit;
  }
}
